import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";
import { FormGroup } from "../../components/Form/styles";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export const Container = styled.div`
  padding-bottom: 2rem;
`;

/* A titled block of the screen: small uppercase label + card (same as /settings) */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 2rem 0;

  @media ${MOBILE} {
    padding: 20px 20px 0;
  }
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 20px;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  /* The card's gap already spaces the fields */
  ${FormGroup} {
    margin-bottom: 0;
  }
`;

/* Submit for the fields above, right-aligned at the bottom of the card */
export const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--color-neutral-base);
`;

/* The two ways to reach the support: side by side on desktop, stacked on phones */
export const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

/* A large support option; the PLUS one (chat) gets the green glow of the other PLUS blocks */
export const OptionCard = styled.button<{ $plus?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  width: 100%;
  min-height: 190px;
  padding: 1.5rem;
  text-align: left;
  background: var(--color-neutral-dark);
  border-radius: 1rem;
  transition: filter 0.15s;

  &:hover {
    filter: brightness(1.1);
  }

  @media ${MOBILE} {
    min-height: 0;
    padding: 20px;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin-bottom: auto;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 12px;

    svg {
      width: 22px;
      height: 22px;
    }

    @media ${MOBILE} {
      margin-bottom: 0.5rem;
    }
  }

  .tag {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;

    @media ${MOBILE} {
      top: 20px;
      right: 20px;
    }
  }

  h4 {
    font-family: Poppins, sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.5rem;
    color: var(--color-neutral-white);
  }

  p {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--color-neutral-150);
  }

  ${props =>
    props.$plus &&
    css`
      background:
        radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.2) 0%, rgba(87, 242, 139, 0) 60%),
        var(--color-neutral-dark);
      box-shadow: inset 0 0 0 1px rgba(87, 242, 139, 0.3);

      .icon {
        color: var(--color-brand-plus);
        background: rgba(87, 242, 139, 0.15);
      }
    `}
`;

/* The embedded support chat: fills the screen below the header (and above the bottom nav on phones) */
export const ChatArea = styled.div`
  height: max(480px, calc(100dvh - 12rem));
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  @media ${MOBILE} {
    height: max(420px, calc(100dvh - 10rem - var(--app-bottom-nav-height, 0px)));
  }

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
